/**
 * 검색 채팅 훅
 *
 * Backend Realtime API를 사용한 웹 검색 채팅 기능
 * - WebSocket 연결 관리
 * - 메시지 전송/수신
 * - 검색 상태 관리 (handleMessage에서 단일 처리)
 * - Zustand Store 연동
 */

import { useCallback, useEffect, useRef } from 'react';
import { useBackendRealtime } from '@/entities/backend-realtime';
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent';
import type { ServerMessage, RagContextMessage } from '@/entities/backend-realtime';
import type { ChatMessageType } from '@/features/text-chat-with-agent/types/chat-message';
import { generateUUID } from '@/shared/utils/uuid';

export interface UseSearchChatProps {
  agentId: string;
  avatarId: string;
  autoConnect?: boolean;
  token?: string;
}

export interface UseSearchChatReturn {
  // 연결 상태
  isConnected: boolean;
  isSearchEnabled: boolean;
  searchState: 'idle' | 'searching' | 'completed';

  // 액션
  connect: () => Promise<void>;
  disconnect: () => void;
  sendMessage: (message: string) => void;
  setSearchEnabled: (enabled: boolean) => void;
}

export function useSearchChat({
  agentId,
  avatarId,
  autoConnect = false,
  token,
}: UseSearchChatProps): UseSearchChatReturn {
  // Zustand Store
  const setSearchState = useRealtimeAgentStore((state) => state.setSearchState);
  const setSearchEnabled = useRealtimeAgentStore((state) => state.setSearchEnabled);
  const isSearchEnabled = useRealtimeAgentStore((state) => state.isSearchEnabled);
  const searchState = useRealtimeAgentStore((state) => state.searchState);
  const realtimeProvider = useRealtimeAgentStore((state) => state.realtimeProvider);
  const addUserMessage = useRealtimeAgentStore((state) => state.addUserMessage);
  const addStreamingAgentMessage = useRealtimeAgentStore((state) => state.addStreamingAgentMessage);
  const updateStreamingAgentMessage = useRealtimeAgentStore((state) => state.updateStreamingAgentMessage);
  const finalizeStreamingMessage = useRealtimeAgentStore((state) => state.finalizeStreamingMessage);
  const setLoading = useRealtimeAgentStore((state) => state.setLoading);
  const setBackendRealtimeSessionId = useRealtimeAgentStore((state) => state.setBackendRealtimeSessionId);

  // 현재 응답 텍스트 추적
  const currentTurnIdRef = useRef<string | null>(null);
  const currentResponseRef = useRef<string>('');
  const currentContextUsedRef = useRef<ChatMessageType['context_used'] | null>(null);
  const searchResetTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 메시지 핸들러 (검색 이벤트는 여기서만 단일 처리)
  const handleMessage = useCallback((message: ServerMessage) => {
    switch (message.type) {
      case 'session.ready':
        console.log('[SearchChat] Session ready');
        setBackendRealtimeSessionId(message.session_id as string | null);
        break;

      case 'response.text.delta':
        if (currentTurnIdRef.current) {
          currentResponseRef.current += message.delta;
          updateStreamingAgentMessage(currentResponseRef.current, currentTurnIdRef.current);
        }
        break;

      case 'response.done':
        if (currentTurnIdRef.current && currentResponseRef.current) {
          finalizeStreamingMessage(
            currentResponseRef.current,
            currentTurnIdRef.current,
            currentContextUsedRef.current ?? undefined
          );
        }
        // 컨텍스트 초기화
        currentContextUsedRef.current = null;
        setLoading(false);
        break;

      case 'rag.context':
        // RAG 컨텍스트 저장 (BM25/Vector 출처 표시용)
        console.log('[SearchChat] RAG context received:', (message as RagContextMessage).context_used?.count, 'documents');
        currentContextUsedRef.current = (message as RagContextMessage).context_used;
        break;

      case 'search.started':
        setSearchState('searching', message.query as string | undefined);
        break;

      case 'search.completed':
        setSearchState('completed', message.query as string | undefined);
        // 기존 타이머 정리 후 3초 뒤 idle 전환
        if (searchResetTimerRef.current) {
          clearTimeout(searchResetTimerRef.current);
        }
        searchResetTimerRef.current = setTimeout(() => {
          setSearchState('idle');
          searchResetTimerRef.current = null;
        }, 3000);
        break;

      case 'error':
        console.error('[SearchChat] Error:', message.message);
        setLoading(false);
        break;
    }
  }, [setSearchState, setBackendRealtimeSessionId, updateStreamingAgentMessage, finalizeStreamingMessage, setLoading]);

  // 텍스트 델타는 handleMessage의 response.text.delta에서 단일 처리
  // (BackendRealtimeAPI의 onTextDelta 콜백은 사용하지 않음)

  // Backend Realtime API 훅 (onSearchStart/onSearchComplete 제거 - handleMessage에서 처리)
  // realtimeProvider에 따라 OpenAI Realtime 또는 Gemini Live API 사용
  const {
    isConnected,
    connect: connectApi,
    disconnect: disconnectApi,
    sendTextWithRag,
    setSearchEnabled: setApiSearchEnabled,
    sessionId,
  } = useBackendRealtime({
    agentId,
    avatarId,
    enableWebSearch: isSearchEnabled,
    autoConnect,
    token,
    provider: realtimeProvider !== 'none' ? realtimeProvider : undefined,
    onMessage: handleMessage,
    onError: (error) => {
      console.error('[SearchChat] API Error:', error);
      setLoading(false);
    },
  });

  // 연결
  const connect = useCallback(async () => {
    await connectApi();
  }, [connectApi]);

  // 연결 해제
  const disconnect = useCallback(() => {
    disconnectApi();
    setBackendRealtimeSessionId(null);
  }, [disconnectApi, setBackendRealtimeSessionId]);

  // 메시지 전송 (전송 성공 확인 후 UI 업데이트)
  const sendMessage = useCallback((message: string) => {
    if (!message.trim()) return;

    // API 전송 (RAG 포함) - 먼저 전송 성공 확인
    const sent = sendTextWithRag(message);
    if (!sent) {
      console.error('[SearchChat] Failed to send message - not connected');
      return;
    }

    // 전송 성공 후 UI 업데이트 (ghost 메시지 방지)
    const newTurnId = generateUUID();
    currentTurnIdRef.current = newTurnId;
    currentResponseRef.current = '';
    currentContextUsedRef.current = null; // 새 메시지 시작 시 컨텍스트 초기화

    addUserMessage(message);
    addStreamingAgentMessage('', newTurnId);
    setLoading(true);
  }, [addUserMessage, addStreamingAgentMessage, setLoading, sendTextWithRag]);

  // 검색 모드 설정
  const handleSetSearchEnabled = useCallback((enabled: boolean) => {
    setSearchEnabled(enabled);
    if (isConnected) {
      setApiSearchEnabled(enabled);
    }
  }, [setSearchEnabled, setApiSearchEnabled, isConnected]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      disconnectApi();
      if (searchResetTimerRef.current) {
        clearTimeout(searchResetTimerRef.current);
      }
    };
  }, [disconnectApi]);

  return {
    isConnected,
    isSearchEnabled,
    searchState,
    connect,
    disconnect,
    sendMessage,
    setSearchEnabled: handleSetSearchEnabled,
  };
}
